/**
 * SubscriptionContext
 * Tracks the current user's subscription tier throughout the app.
 *
 * Tier hierarchy:
 *   free → pro → enterprise
 *
 * Each tier unlocks progressively more features:
 * - free:       Heuristic model, basic map, timeline scrubber
 * - pro:        ML/Physics models, haptics, climate overlay, VAR gap, chatbot alerts
 * - enterprise: Consensus engine, IPCC model, model store API, white-label, Quote Me
 *
 * The context also provides a `gate()` function that checks if the user's tier
 * can access a given feature. If not, it returns { allowed: false, requiredTier }
 * so the calling component can show an upgrade prompt.
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export type SubscriptionTier = 'free' | 'pro' | 'enterprise';

export interface SubscriptionPlan {
  tier: SubscriptionTier;
  name: string;
  price: string;
  priceMonthly: number;
  tagline: string;
  features: string[];
  highlighted?: boolean;
}

export interface GateResult {
  allowed: boolean;
  requiredTier?: SubscriptionTier;
}

interface SubscriptionContextValue {
  /** Current subscription tier */
  tier: SubscriptionTier;
  /** Set tier (for demo/testing — production would come from API) */
  setTier: (tier: SubscriptionTier) => void;
  /** Check if the current tier can access a feature requiring `requiredTier` */
  gate: (requiredTier: SubscriptionTier) => GateResult;
  /** Check if a model tier string is accessible */
  canAccessModelTier: (modelTier: 'free' | 'premium' | 'enterprise') => boolean;
  /** Whether the upgrade modal should be shown */
  showUpgradeModal: boolean;
  /** The tier being upsold (what triggered the gate) */
  upsellTier: SubscriptionTier | null;
  /** Context about what triggered the upgrade prompt */
  upsellContext: string | null;
  /** Request an upgrade — opens the modal with context */
  requestUpgrade: (requiredTier: SubscriptionTier, context?: string) => void;
  /** Dismiss the upgrade modal */
  dismissUpgrade: () => void;
  /** Simulate an upgrade (for demo) */
  simulateUpgrade: (tier: SubscriptionTier) => void;
}

// ============================================================================
// Plan Catalog
// ============================================================================

export const PLANS: SubscriptionPlan[] = [
  {
    tier: 'free',
    name: 'Firebreak',
    price: 'Free',
    priceMonthly: 0,
    tagline: 'Wildfire risk intelligence',
    features: [
      'Heuristic distance model',
      'Interactive map + timeline',
      'Historical fire perimeters',
      'Basic VARTracker dashboard',
      'Up to 10 properties',
    ],
  },
  {
    tier: 'pro',
    name: 'Firebreak Pro',
    price: '$29/mo',
    priceMonthly: 29,
    tagline: 'Advanced risk analysis',
    highlighted: true,
    features: [
      'Everything in Free, plus:',
      'ML Wildfire v3 model',
      'Physics FARSITE simulation',
      'Haptic timeline feedback',
      'CO2 / IPCC climate overlay',
      'VAR Gap analysis (Policy vs Property)',
      'Proactive chatbot alerts',
      'Unlimited properties',
      'CSV / JSON / PDF export',
    ],
  },
  {
    tier: 'enterprise',
    name: 'Firebreak Enterprise',
    price: 'Custom',
    priceMonthly: -1,
    tagline: 'Full portfolio risk management',
    features: [
      'Everything in PRO, plus:',
      'Consensus Engine (model blend)',
      'IPCC Climate-Adjusted model',
      'Model Store API access',
      'White-label / multi-tenant',
      '"Quote Me" instant bind',
      'Dedicated support + SLA',
      'Custom model onboarding',
    ],
  },
];

// ============================================================================
// Tier Ordering
// ============================================================================

const TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 1,
  enterprise: 2,
};

/** Map model tier labels to subscription tiers */
const MODEL_TIER_MAP: Record<string, SubscriptionTier> = {
  free: 'free',
  premium: 'pro',
  enterprise: 'enterprise',
};

// ============================================================================
// Context
// ============================================================================

const SubscriptionContext = createContext<SubscriptionContextValue>({
  tier: 'free',
  setTier: () => {},
  gate: () => ({ allowed: true }),
  canAccessModelTier: () => true,
  showUpgradeModal: false,
  upsellTier: null,
  upsellContext: null,
  requestUpgrade: () => {},
  dismissUpgrade: () => {},
  simulateUpgrade: () => {},
});

// ============================================================================
// Provider
// ============================================================================

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [tier, setTier] = useState<SubscriptionTier>('free');
  // IMPORTANT: Always initialize modal state to false to prevent stuck overlays
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upsellTier, setUpsellTier] = useState<SubscriptionTier | null>(null);
  const [upsellContext, setUpsellContext] = useState<string | null>(null);

  // Safety: Force close modal on initial mount (fix for stuck modal bug)
  useEffect(() => {
    setShowUpgradeModal(false);
    if (import.meta.env.DEV) console.log('[SubscriptionContext] Initialized - modal state reset');
  }, []);

  const gate = useCallback(
    (requiredTier: SubscriptionTier): GateResult => {
      if (TIER_RANK[tier] >= TIER_RANK[requiredTier]) {
        return { allowed: true };
      }
      return { allowed: false, requiredTier };
    },
    [tier]
  );

  const canAccessModelTier = useCallback(
    (modelTier: 'free' | 'premium' | 'enterprise'): boolean => {
      const required = MODEL_TIER_MAP[modelTier] ?? 'free';
      return TIER_RANK[tier] >= TIER_RANK[required];
    },
    [tier]
  );

  const requestUpgrade = useCallback(
    (requiredTier: SubscriptionTier, context?: string) => {
      setUpsellTier(requiredTier);
      setUpsellContext(context ?? null);
      setShowUpgradeModal(true);
    },
    []
  );

  const dismissUpgrade = useCallback(() => {
    setShowUpgradeModal(false);
    setUpsellTier(null);
    setUpsellContext(null);
  }, []);

  const simulateUpgrade = useCallback((newTier: SubscriptionTier) => {
    setTier(newTier);
    setShowUpgradeModal(false);
    setUpsellTier(null);
    setUpsellContext(null);
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        tier,
        setTier,
        gate,
        canAccessModelTier,
        showUpgradeModal,
        upsellTier,
        upsellContext,
        requestUpgrade,
        dismissUpgrade,
        simulateUpgrade,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export default SubscriptionContext;
